// LoginForm.tsx
import type { FormEvent } from 'react';
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import InputGroup from 'react-bootstrap/InputGroup';
import { Alert, Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faInfoCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import type { SelectMap } from '../../../../MedicareProFront/Utilities/SelectMap';

type SignUpValues = { username: string };

type SignUpFormProps = {
  onSubmit?: (values: SignUpValues) => Promise<void> | void;
  submitLabel?: string;
  errorMessage?: string;
};

export const SignUpForm = ({ onSubmit, submitLabel = 'Send', errorMessage }: SignUpFormProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [platform, setPlatform] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [username, setUsername] = useState('');
  const [touched, setTouched] = useState({
    username: false,
    firstName: false,
    lastName: false,
    platform: false,
    companyName: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Simple, reliable email check (good enough for inline UX)
  const emailIsValid = username.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username);

  const formIsValid = emailIsValid;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({
      username: true,
      firstName: true,
      lastName: true,
      platform: true,
      companyName: true,
    });
    if (!formIsValid) return;

    try {
      setSubmitting(true);
      await onSubmit?.({ username });
    } finally {
      setSubmitting(false);
    }
  }

  const platforms: Array<SelectMap> = [
    {
      label: 'Agent',
      value: 'agent',
    },
    {
      label: 'Agency',
      value: 'agency',
    },
  ];

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <Row>
        <Col lg={6}>
          {/* First Name */}
          <Form.Group className="mb-3">
            <Form.Label>First Name*</Form.Label>

            <Form.Control
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
              isValid={firstName && touched.firstName}
              isInvalid={!firstName && touched.firstName}
              autoComplete="firstName"
              required
            />
            <Form.Control.Feedback type="invalid">Please enter a first name</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          {/* Last Name */}
          <Form.Group className="mb-3">
            <Form.Label>Last Name*</Form.Label>

            <Form.Control
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
              isValid={lastName && touched.lastName}
              isInvalid={!lastName && touched.lastName}
              autoComplete="lastName"
              required
            />
            <Form.Control.Feedback type="invalid">Please enter a last name</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          {/* Company Name */}
          <Form.Group className="mb-3">
            <Form.Label>Company Name*</Form.Label>

            <Form.Control
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, companyName: true }))}
              isValid={companyName && touched.companyName}
              isInvalid={!companyName && touched.companyName}
              autoComplete="companyName"
              required
            />
            <Form.Control.Feedback type="invalid">
              Please enter a company name
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={12}>
          {/* Platform */}
          <Form.Group className="mb-3">
            <Form.Label>
              Platform
              <OverlayTrigger
                trigger="click"
                placement="right"
                rootClose
                overlay={
                  <Popover>
                    <Popover.Body>
                      <strong>Agent Platform</strong> - Mange your book of business
                      <br />
                      <strong>Agency Platform</strong> - Agent platform + mange your downline agents
                      and agencies.
                    </Popover.Body>
                  </Popover>
                }
              >
                <FontAwesomeIcon className="ms-2" icon={faInfoCircle} />
              </OverlayTrigger>
            </Form.Label>

            <Form.Select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, platform: true }))}
              required
            >
              {platforms.map((item) => (
                <option value={item.value}>{item.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col lg={12}>
          <Button
            type="submit"
            variant="primary"
            disabled={!formIsValid || submitting}
            className="w-100"
          >
            {submitLabel} <FontAwesomeIcon icon={faArrowRight} />
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
